import {
  CERT_FOR_TRACK,
  TRACK_OPTIONS,
  type ContentCertLevel,
  type TrackOption,
  type UserTrack,
} from "@/lib/onboarding/constants";

/**
 * Certs not yet launched to the public — visible only to internal users.
 *
 * This set is the single launch switch for a cert: a value here means that
 * cert's content (scenarios, questions, everything keyed on `cert_level`) is
 * hidden from non-internal users on every read path. **To launch a cert to the
 * public, delete it from this set — one line.**
 *
 * Kept in sync with the SQL mirror `cert_is_internal_only`. 2026-07: the WHOLE
 * cert line is now PUBLIC — EMT/AEMT/Paramedic/CNA/LPN/RN/CCRN/CEN + RRT/CRT + the
 * FNP boards (FNP-C AANP + FNP-BC ANCC), launched public 2026-07-08. So this set is
 * EMPTY. The gate stays wired: when a future internal-only cert lands, add it here
 * AND to the SQL mirror. This is the load-bearing app-layer gate for the
 * admin-client content reads that bypass RLS, so any un-launched cert MUST be here
 * or its content leaks (the SQL mirror only guards the count/topics RPCs).
 */
export const INTERNAL_ONLY_CERTS: ReadonlySet<ContentCertLevel> =
  new Set<ContentCertLevel>([]);

/** Internal-only-cert axis shared by the gate + the dropdown list below. */
function trackCertVisibleTo(track: UserTrack, isInternal: boolean): boolean {
  return isInternal || !INTERNAL_ONLY_CERTS.has(CERT_FOR_TRACK[track]);
}

/**
 * Whether a viewer may PICK a study track — the server boundary behind both
 * actions (onboarding + settings switcher). Enforces BOTH selectability
 * axes: the internal-only-cert gate AND `TrackOption.available` (a
 * "coming soon" teaser renders in the dropdown as disabled, but a crafted
 * POST must not enroll anyone in it — a disabled attribute is not a
 * boundary). Tracks absent from TRACK_OPTIONS (legacy `Nurse`/`MedStudent`
 * enum values) are not selectable; the Zod enum already rejects those
 * before this gate runs.
 */
export function trackIsSelectable(
  track: UserTrack,
  isInternal: boolean,
): boolean {
  const option = TRACK_OPTIONS.find((o) => o.value === track);
  if (!option?.available) return false;
  return trackCertVisibleTo(track, isInternal);
}

/** User-facing copy both server actions return when the gate above rejects
 *  a pick — one owner so onboarding and settings can't drift. */
export const TRACK_UNAVAILABLE_MESSAGE = "That track isn't available yet.";

/**
 * TRACK_OPTIONS narrowed to what this viewer's dropdown should RENDER.
 * Deliberately looser than {@link trackIsSelectable}: it filters only the
 * internal-only-cert axis, so an `available: false` teaser track stays
 * visible-but-disabled (`disabled={!opt.available}` in TrackSelectField)
 * instead of vanishing. Invariant: everything the GATE accepts is in this
 * LIST; the list may additionally render (disabled) entries the gate
 * rejects.
 */
export function selectableTrackOptions(
  isInternal: boolean,
): ReadonlyArray<TrackOption> {
  return TRACK_OPTIONS.filter((o) => trackCertVisibleTo(o.value, isInternal));
}

/**
 * The cert level a viewer may browse in list / catalog contexts. Never returns
 * an internal-only cert for a non-internal user — it falls back to `"EMT"` so
 * the catalog and question library are never blank. (A stale Paramedic-track
 * enrollment on a non-internal account must not zero out their whole app.)
 *
 * Feed this to every list query's `.eq("cert_level", ...)` filter.
 */
export function visibleCatalogCert(
  viewerCert: ContentCertLevel,
  isInternal: boolean,
): ContentCertLevel {
  if (INTERNAL_ONLY_CERTS.has(viewerCert) && !isInternal) return "EMT";
  return viewerCert;
}

/**
 * The cert to filter a list/catalog query by, or `null` meaning "no cert
 * filter — show every cert". Internal users get `null` so one internal account
 * can browse/test every vertical (EMT + Paramedic + future nursing certs);
 * non-internal users stay narrowed to their single browsable cert via
 * {@link visibleCatalogCert}.
 *
 * Security: `null` is returned ONLY for an internal caller — so it only ever
 * widens what *internal* sees. For a non-internal caller it returns a concrete
 * non-internal-safe cert (internal-only certs fall back to `"EMT"`), NEVER `null`.
 * That app-side value is the real boundary for the admin/service_role list reads
 * (`getAdminSupabase()`) that bypass RLS, so this helper MUST NEVER return `null`
 * for a non-internal caller. The SECURITY DEFINER RPCs (`count_questions_for_user`,
 * `get_clinical_topics`) add an independent server-side gate
 * (`(select public.is_internal()) or not public.cert_is_internal_only(...)`) on
 * their own paths regardless of what the app passes.
 *
 * Usage: `const cf = catalogCertFilter(cert, isInternal); if (cf) query = query.eq("cert_level", cf);`
 * or pass `cf` straight to an RPC's nullable `p_cert_level` (null = all certs).
 */
export function catalogCertFilter(
  viewerCert: ContentCertLevel,
  isInternal: boolean,
): ContentCertLevel | null {
  if (isInternal) return null;
  return visibleCatalogCert(viewerCert, isInternal);
}

/**
 * Whether a single content item (identified by its own `cert_level`) is visible
 * to a viewer. Defined in terms of {@link catalogCertFilter} so the per-item
 * deep-link gate and the catalog filter can **never disagree**: an item is
 * visible iff its cert equals the viewer's effective browsable cert.
 *
 * This is exact match — an AEMT viewer does not see EMT content, mirroring the
 * catalog's `.eq("cert_level", ...)` — with the internal-only override folded
 * in via {@link visibleCatalogCert}. Use at every deep-link / detail / start
 * gate, and return `notFound()` (not 403) when false so the resource's
 * existence isn't leaked.
 */
export function isContentVisible(
  contentCert: string | null | undefined,
  viewerCert: ContentCertLevel,
  isInternal: boolean,
): boolean {
  // A missing cert (some projections type cert_level nullable, though the
  // column is NOT NULL) is treated as not-visible — the safe default.
  if (contentCert == null) return false;
  // Single source of truth with the list filter: internal → catalogCertFilter
  // returns null (sees every cert); non-internal → exact-match on their cert.
  const filter = catalogCertFilter(viewerCert, isInternal);
  if (filter === null) return true;
  return contentCert === filter;
}
